import { IkeRestDepartment } from '@ikegps/core-shared';
import { observable, runInAction, makeObservable } from 'mobx';
import type { MithrilVNode } from '../../mithril';
import { SelectInputComponent, SelectInputState } from '../../components/input/input.select';
import { InputState } from '../../components/input/input.state';
import { ModalFormComponent } from '../../components/modal.form/modal.form.component';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles, DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m } from '../../mithril';
import { Logger, Services } from '../../services';
import { UniversalFileUploadReader } from '../../util/upload.reader';
import { FormImportCtrl } from './form.import.ctrl';
import { openValidationNotification } from '../validation.notification/validation.notification';

const FORM_IMPORT_SUCCEED = { key: T.FORM_IMPORT_SUCCEED };

export const FormImportModalCSS = {
	container: 'c-FormImportModal',
	text: 'c-FormImportModal__text',
	file: 'c-FormImportModal__file',
	form: 'c-FormImportModal__form',
	copyTitle: 'c-FormImportModal__copy-title',
	isActive: 'c-FormImportModal--is-active',
};

addStyles({
	[FormImportModalCSS.form]: {
		width: '100%',
	},
	[FormImportModalCSS.container]: {
		width: '100%',
		height: '100%',
		display: 'flex',
		'justify-content': 'center',
		'align-items': 'center',
		border: '1px solid ' + DefaultTheme.font.color.disabled,
		'&:hover': {
			border: '1px solid ' + DefaultTheme.color.accent,
			cursor: 'pointer',
		},
	},
	[FormImportModalCSS.file]: {
		display: 'none',
	},
	[FormImportModalCSS.copyTitle]: {
		marginTop: '10px',
	},
});

export class FormImportModal extends ModalFormComponent {
	departmentSelectState: SelectInputState;

	@observable loadingSecondaryForms: boolean = false;

	secondaryFormSelectState: SelectInputState;

	fileInputState: InputState<UniversalFileUploadReader<void>>;

	ctrl: FormImportCtrl;

	constructor(ctrl: FormImportCtrl) {
		super({
			title: T.FORM_IMPORT,
			confirm: T.FORM_IMPORT_CONFIRM,
			success: FORM_IMPORT_SUCCEED,
		});

		makeObservable(this);

		this.ctrl = ctrl;
		this.displayCustomButtons = true;

		this.departmentSelectState = new SelectInputState('Department');
		this.departmentSelectState.onValue(() => this.loadSecondaryForms());

		for (const dept of Services.Department.departments) {
			this.departmentSelectState.addOption({ key: dept.name, value: dept.objectId });
		}

		this.secondaryFormSelectState = new SelectInputState('Form');
		this.secondaryFormSelectState.disabled = true;
		this.secondaryFormSelectState.onValue((formID) => {
			this.ctrl.secondaryFormId = formID;
		});

		this.fileInputState = new InputState('File');
		this.fileInputState.isRequired = true;
		this.fileInputState.reaction(
			() => this.ctrl.file && this.ctrl.file.buffer,
			() => {
				if (this.ctrl.file) {
					this.fileInputState.setValue(this.ctrl.file);
				} else {
					this.fileInputState.setError('File is required.');
				}
			}
		);

		this.addState(this.fileInputState);
	}

	private async loadSecondaryForms() {
		if (!this.departmentSelectState.value) {
			return;
		}

		runInAction(() => {
			this.loadingSecondaryForms = true;
		});

		const deptRest = new IkeRestDepartment(Services.User.rest, this.departmentSelectState.value);

		const forms = await deptRest.formSummaries.fetch();

		this.secondaryFormSelectState.reset();
		this.secondaryFormSelectState.setOptions([]);

		for (const form of forms) {
			this.secondaryFormSelectState.addOption({ key: form.displayname, value: form.revision });
		}

		runInAction(() => {
			this.loadingSecondaryForms = false;
		});
	}

	get nodes() {
		let className = FormImportModalCSS.container;

		if (this.ctrl.isActive) {
			className += ' ' + FormImportModalCSS.isActive;
		}

		return m('form', { className: FormImportModalCSS.form }, [
			...(this.ctrl.file
				? [m('div', [this.ctrl.file.name])]
				: [
						m(
							'input',
							{
								className: FormImportModalCSS.file,
								type: 'file',
								id: this.ctrl.fileId,
								onchange: this.ctrl.onFile,
								accept: FormImportCtrl.ALLOWED_SUFFIXES.join(', '),
							},
							''
						),
						m(
							'div',
							{
								className,
								onclick: this.ctrl.openFileDialog,
							},
							[Translate(T.FORM_IMPORT_UPLOAD_BUTTON)]
						),
					]),
			m('div', { className: FormImportModalCSS.copyTitle }, [Translate(T.FORM_NEW_COPY_CUSTOM_FIELDS_FROM)]),
			SelectInputComponent.view(this.departmentSelectState),
			this.loadingSecondaryForms ? Spinner.view() : SelectInputComponent.view(this.secondaryFormSelectState),
		]);
	}

	override customButtons(): MithrilVNode[] {
		const submitChildren = this.loading ? Spinner.view() : Translate(T.FORM_IMPORT_CONFIRM);

		return [
			m(
				'button',
				{ className: 'mdl-button', onclick: () => this.close(), disabled: this.loading, type: 'button' },
				Translate(T.CANCEL)
			),
			m(
				'button',
				{
					className: 'mdl-button mdl-button--accent',
					onclick: () => this.handleImport(),
					disabled: !this.form.canSubmit || this.loading,
					type: 'button',
				},
				submitChildren
			),
		];
	}

	private async handleImport() {
		this.setLoading(true);

		try {
			// Step 1: Validate only
			await this.ctrl.validateFile();

			const hasWarnings = this.ctrl.validationWarnings.length > 0;
			const hasInfo = this.ctrl.validationInfo.length > 0;

			if (hasWarnings || hasInfo) {
				// Copy validation data before modal close resets state
				const warnings = [...this.ctrl.validationWarnings];
				const info = [...this.ctrl.validationInfo];

				// Close import modal so validation notification can open
				this.close();

				const result = await openValidationNotification({ warnings, info });
				if (result.ok) {
					// Step 2: User confirmed - create the form
					await this.ctrl.createForm();
					NotificationService.success(FORM_IMPORT_SUCCEED);
				}
				return;
			}

			// No warnings - create form directly
			await this.ctrl.createForm();
			NotificationService.success(FORM_IMPORT_SUCCEED);
			this.close();
		} catch (e) {
			Logger.error(e);
			this.setLoading(false);

			if (this.ctrl.validationErrors.length > 0) {
				// Show error notification modal (closes import modal)
				openValidationNotification({
					errors: this.ctrl.validationErrors,
					warnings: this.ctrl.validationWarnings,
					info: this.ctrl.validationInfo,
				});
			} else {
				NotificationService.error({ key: T.ERROR_GENERAL });
			}
		}
	}
}
